/**
 * Validation utilities for car hire approval flow.
 * Determines when an approval is invalid and when to clear it.
 */

import { isOverBudget } from './budgetComparison'

export type CarHireApprovalInvalidReason =
  | 'offer'
  | 'location_dates'
  | 'budget'
  | undefined

const CART_EVALUATION_REASON_TO_INVALID_REASON: Record<string, CarHireApprovalInvalidReason> = {
  OFFER: 'offer',
  LOCATION_DATES: 'location_dates',
  BUDGET: 'budget',
}

type GetCarHireApprovalInvalidReasonParams = {
  approvalRequest: App.BusinessTraveller.ApprovalRequest | undefined
  approvalPolicy: App.BusinessTraveller.ApprovalPolicy | null
  carHireViews: Array<App.Checkout.CarHireItemView>
  carHireTotal: number
  cartEvaluationTripRequest?: App.BusinessTraveller.CartEvaluationTripRequestValidation | null
}

export function getCarHireApprovalInvalidReason(params: GetCarHireApprovalInvalidReasonParams): CarHireApprovalInvalidReason {
  // Item policies on: the backend cart evaluation owns the verdict
  if (params.cartEvaluationTripRequest !== undefined) {
    const tripRequest = params.cartEvaluationTripRequest
    return tripRequest && !tripRequest.isValid
      ? tripRequest.invalidReasons.map((entry) => CART_EVALUATION_REASON_TO_INVALID_REASON[entry.reason]).find(Boolean)
      : undefined
  }

  return getLegacyInvalidReason(params)
}

/** @deprecated remove when the lebt_item_policies_enabled flag is on in production */
function getLegacyInvalidReason(params: GetCarHireApprovalInvalidReasonParams): CarHireApprovalInvalidReason {
  const { approvalRequest, approvalPolicy, carHireViews, carHireTotal } = params

  if (!approvalRequest || !approvalPolicy || carHireViews.length === 0) {
    return undefined
  }

  const carHireApprovalItem = approvalRequest.tripRequestItems?.find(
    (item): item is App.BusinessTraveller.CarHireTripRequestItem => item.type === 'car_hire',
  )

  if (!carHireApprovalItem) {
    return undefined
  }

  const carHireView = carHireViews[0]
  const item = carHireView.item

  // Check offer match (different car selected)
  const approvedOfferId = carHireApprovalItem.displayData?.carHireDetails?.offerId
  if (approvedOfferId && item.offerId !== approvedOfferId) {
    return 'offer'
  }

  // Check location/dates match
  const approvalDetails = carHireApprovalItem.displayData?.carHireDetails
  const displayData = carHireApprovalItem.displayData
  const pickUpIdMismatch =
    approvalDetails?.pickUpId && item.pickUpLocationId !== approvalDetails.pickUpId
  const dropOffIdMismatch =
    approvalDetails?.dropOffId && item.dropOffLocationId !== approvalDetails.dropOffId
  const pickupDateMismatch =
    displayData?.pickupDate && item.pickUpDate !== displayData.pickupDate
  const dropoffDateMismatch =
    displayData?.dropoffDate && item.dropOffDate !== displayData.dropoffDate

  if (pickUpIdMismatch || dropOffIdMismatch || pickupDateMismatch || dropoffDateMismatch) {
    return 'location_dates'
  }

  // Check budget (price exceeded approved total + buffer)
  const approvalLimit = approvalRequest.total ?? 0
  if (approvalLimit > 0) {
    const priceBufferPercent = approvalPolicy?.priceBuffer ?? 0
    const maxAllowed = approvalLimit + (approvalLimit * priceBufferPercent) / 100
    if (isOverBudget(carHireTotal, maxAllowed)) {
      return 'budget'
    }
  }

  return undefined
}

export function isCarHireOutOfBudget(
  carHireTotal: number,
  carHireBudgetTotal: number,
  treatMissingBudgetAsOutOfBudget: boolean = false,
): boolean {
  if (treatMissingBudgetAsOutOfBudget && carHireBudgetTotal <= 0) {
    return true
  }
  return carHireBudgetTotal > 0 && isOverBudget(carHireTotal, carHireBudgetTotal)
}

/**
 * When policy scope is NEVER and user has invalid approval (e.g. over budget),
 * clear approval so they proceed to payment without blocking.
 */
export function shouldClearApprovalForNeverScopeWhenOverBudget(
  approvalPolicy: App.BusinessTraveller.ApprovalPolicy | null,
  invalidReason: CarHireApprovalInvalidReason,
  totalWithoutServiceFee: number,
  companyBudget: number,
): boolean {
  if (!approvalPolicy || approvalPolicy.scope !== 'NEVER' || !invalidReason) {
    return false
  }
  if (invalidReason === 'budget' && companyBudget > 0 && isOverBudget(totalWithoutServiceFee, companyBudget)) {
    return true
  }
  return false
}
